import { useMemo } from "react"

const getNormalizedString = (value) => {
  if (value === null || value === undefined) {
    return ""
  }
  return `${value}`.trim()
}

const getComparableIdentifier = (value) => getNormalizedString(value).toLowerCase()

const getNormalizedSet = (values) => {
  if (!Array.isArray(values)) {
    return new Set()
  }

  return new Set(values.map((value) => getComparableIdentifier(value)).filter(Boolean))
}

// `a || b || c` stops at the first *truthy* value, and an empty array `[]` is truthy in JS.
// That meant a module with an empty `sch_codes_with_access` field would never fall through to
// check `schools_with_access` or `module_json.schoolsWithAccess`, wrongly denying school members
// access. This picks the first candidate that actually has entries.
const getFirstNonEmptyArray = (...candidates) => candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0) || []

const getIsWingspansAdmin = ({ isWingspansAdmin, profile }) => {
  if (isWingspansAdmin) {
    return true
  }

  const normalizedRole = getNormalizedString(profile?.role).toLowerCase()
  const normalizedMembershipType = getNormalizedString(profile?.membershipType).toLowerCase()

  return (
    normalizedRole === "admin" ||
    normalizedRole === "super-admin" ||
    normalizedRole === "super admin" ||
    normalizedMembershipType === "admin" ||
    normalizedMembershipType === "super-admin" ||
    normalizedMembershipType === "super admin"
  )
}

export const getModuleAuthorization = ({ moduleTemplate, userSQLId, profile, schoolMemberships, subdomainSchoolId, isWingspansAdmin = false } = {}) => {
  const hasModuleTemplate = Boolean(moduleTemplate)
  const moduleCreatorId = getNormalizedString(moduleTemplate?.creator_id)
  const callerSQLUserId = getNormalizedString(userSQLId)
  const accessType = getNormalizedString(moduleTemplate?.access_type).toLowerCase()

  // Include school codes from profile, from schoolMemberships (which may resolve before profile.schCodes
  // is merged), and from the current subdomain's school id. The subdomain school id is the most reliable
  // signal of "which school this session belongs to" since it's resolved synchronously from the URL and
  // doesn't depend on async profile/membership data having loaded or matched the module's stored format yet.
  const profileSchoolCodes = getNormalizedSet(profile?.schCodes)
  const membershipSchoolCodes = getNormalizedSet(
    Array.isArray(schoolMemberships) ? schoolMemberships.flatMap((m) => [m?.sch_code, m?.id]).filter((v) => v !== undefined && v !== null) : []
  )
  const subdomainSchoolCodes = getNormalizedSet(subdomainSchoolId !== undefined && subdomainSchoolId !== null ? [subdomainSchoolId] : [])
  const userSchoolCodes = new Set([...profileSchoolCodes, ...membershipSchoolCodes, ...subdomainSchoolCodes])

  // Check all known field locations where school access codes can be stored
  const moduleSchoolCodes = getNormalizedSet(
    getFirstNonEmptyArray(
      moduleTemplate?.sch_codes_with_access,
      moduleTemplate?.schools_with_access,
      moduleTemplate?.module_json?.schoolsWithAccess
    )
  )

  const isCreator = Boolean(moduleCreatorId && callerSQLUserId && moduleCreatorId === callerSQLUserId)
  const isAdmin = getIsWingspansAdmin({ isWingspansAdmin, profile })
  const isSchoolMember = [...moduleSchoolCodes].some((schoolCode) => userSchoolCodes.has(schoolCode))

  const isPrivate = accessType === "private"
  const isSchool = accessType === "school"
  const isPublic = accessType === "public"

  const canViewModule = (() => {
    if (!hasModuleTemplate) {
      return false
    }

    if (isPrivate) {
      return isCreator
    }

    if (isSchool) {
      return isCreator || isSchoolMember
    }

    return true
  })()

  const canEditModule = (() => {
    if (isPublic) {
      return isAdmin
    }

    if (isPrivate || isSchool) {
      return isCreator
    }

    return false
  })()

  const canDeleteModule = canEditModule
  const canManageSchoolAccess = isSchool && isCreator

  const getUnauthorizedViewMessage = () => {
    if (canViewModule) {
      return null
    }

    if (!hasModuleTemplate) {
      return "Module was not found"
    }

    if (isPrivate) {
      return "You are not authorized to view this module"
    }

    if (isSchool) {
      return "You are not authorized to view this module"
    }

    return "You are not authorized to view this module"
  }

  const getUnauthorizedEditMessage = () => {
    if (canEditModule) {
      return null
    }

    if (!hasModuleTemplate) {
      return "Module was not found"
    }

    if (isPublic) {
      return "Only Wingspans admins can edit public modules"
    }

    if (isPrivate || isSchool) {
      return "Only the module creator can edit this module"
    }

    return "You are not authorized to edit this module"
  }

  return {
    hasModuleTemplate,
    accessType,
    isAdmin,
    isCreator,
    isPrivate,
    isSchool,
    isPublic,
    isSchoolMember,
    userSchoolCodes: [...userSchoolCodes],
    moduleSchoolCodes: [...moduleSchoolCodes],
    canViewModule,
    canEditModule,
    canDeleteModule,
    canManageSchoolAccess,
    getUnauthorizedViewMessage,
    getUnauthorizedEditMessage,
  }
}

const useModuleAuthorization = ({ moduleTemplate, userSQLId, profile, schoolMemberships, subdomainSchoolId, isWingspansAdmin = false } = {}) => {
  return useMemo(
    () => getModuleAuthorization({ moduleTemplate, userSQLId, profile, schoolMemberships, subdomainSchoolId, isWingspansAdmin }),
    [moduleTemplate, userSQLId, profile, schoolMemberships, subdomainSchoolId, isWingspansAdmin]
  )
}

export default useModuleAuthorization
